import * as React from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "../Label/Label";
import { Checkbox } from "@/components/ui/checkbox";

interface MultiSelectProps {
  options: { value: string; label: string | number }[];
  placeholder: string;
  label?: string;
  disabled?: boolean;
  values?: string[];
  helperText?: string;
  hasError?: boolean;
  cypressName?: string;
  onValuesChange?: (values: string[]) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  placeholder,
  label,
  helperText,
  cypressName,
  disabled,
  onValuesChange,
  values = [],
}) => {
  const getSelectedLabels = () => {
    return values
      .map((value) => options.find((opt) => opt.value === value)?.label)
      .filter(Boolean)
      .join(", ");
  };

  const handleChange = (value: string) => {
    const newValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];

    if (onValuesChange) {
      onValuesChange(newValues);
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {label && <Label>{label}</Label>}
      <Select disabled={disabled}>
        <SelectTrigger
          data-cy={cypressName}
          className={`!min-h-[40px] !mt-0 bg-[#09090b] disabled:bg-bgDisabled text-muted !text-sm ${
            helperText ? "!border-error" : "border-colorBorder"
          }`}
        >
          <span className="line-clamp-1 text-left">{getSelectedLabels() || placeholder}</span>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-cy="select-option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleChange(option.value);
                }}
                className="max-w-sm w-full"
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={values.includes(option.value)}
                    onCheckedChange={() => handleChange(option.value)}
                  />
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {helperText && <p className="text-sm text-error">{helperText}</p>}
    </div>
  );
};

export default MultiSelect;
